import { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useParams, useNavigate, Link } from 'react-router-dom';
import { Dashboard } from '@/pages/Dashboard';
import { Home } from '@/pages/Home';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import { Moon, Sun, Sparkles, ArrowLeft, Home as HomeIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DisclaimerModal } from '@/components/DisclaimerModal';
import { DisclaimerFooter } from '@/components/DisclaimerFooter';
import { StateProvider, useStateContext } from '@/context/StateContext';
import { StateSelector } from '@/components/StateSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Wrapper component to sync URL state param with context
const StateDashboardWrapper = () => {
  const { stateCode } = useParams();
  const { selectState } = useStateContext();
  
  useEffect(() => {
    if (stateCode) {
      selectState(stateCode.toUpperCase());
    }
  }, [stateCode, selectState]);
  
  return <Dashboard />;
};

// Header component for Dashboard pages
const DashboardHeader = ({ isDark, toggleTheme, onRefresh, isRefreshing }) => {
  const { getCurrentStateName } = useStateContext();
  
  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      <div className="flex items-center justify-between h-16 px-4 md:px-6">
        {/* Left - Back to Home + Branding */}
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <HomeIcon className="w-4 h-4" />
          </Link>
          <div className="hidden md:flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold">Scratch Intel</span>
          </div>
        </div>
        
        {/* Center - State Selector */}
        <div className="flex items-center">
          <StateSelector />
        </div>
        
        {/* Right - Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="hidden md:flex"
          >
            {isRefreshing ? 'Updating...' : 'Refresh Data'}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </div>
    </header>
  );
};

// Header component for Home page
const HomeHeader = ({ isDark, toggleTheme }) => {
  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      <div className="flex items-center justify-between h-16 px-4 md:px-6">
        {/* Left - Branding */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-lg">Scratch Intel</span>
        </div>
        
        {/* Right - Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>
    </header>
  );
};

// Layout for Dashboard pages (with state selector)
const DashboardLayout = ({ children, isDark, toggleTheme, onRefresh, isRefreshing }) => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <DashboardHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
      />
      <main className="flex-1">
        {children}
      </main>
      <DisclaimerFooter />
    </div>
  );
};

// Layout for Home page (simpler header)
const HomeLayout = ({ children, isDark, toggleTheme }) => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <HomeHeader isDark={isDark} toggleTheme={toggleTheme} />
      <main className="flex-1">
        {children}
      </main>
      <DisclaimerFooter />
    </div>
  );
};

function AppContent() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const { currentState } = useStateContext();
  
  // Load theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
      setIsDark(false);
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }
  }, []);
  
  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    localStorage.setItem('theme', newIsDark ? 'dark' : 'light');
    if (newIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    try {
      // Use state-specific ingest if not WA
      const ingestUrl = currentState === 'WA' 
        ? `${API}/ingest/run`
        : `${API}/states/${currentState}/ingest`;
      
      const response = await axios.post(ingestUrl);
      
      if (response.data.success) {
        toast.success(`Updated ${response.data.games_saved} games`);
      } else {
        toast.error(response.data.message || 'Update failed');
      }
    } catch (err) {
      console.error('Refresh error:', err);
      toast.error('Failed to update data');
    } finally {
      setIsRefreshing(false);
      // Reload page after a delay
      setTimeout(() => window.location.reload(), 2000);
    }
  };
  
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Home Page */}
          <Route path="/" element={
            <HomeLayout isDark={isDark} toggleTheme={toggleTheme}>
              <Home />
            </HomeLayout>
          } />
          
          {/* State Dashboard Routes */}
          <Route path="/:stateCode" element={
            <DashboardLayout 
              isDark={isDark} 
              toggleTheme={toggleTheme}
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
            >
              <StateDashboardWrapper />
            </DashboardLayout>
          } />
          
          {/* Methodology Page */}
          <Route path="/methodology" element={
            <HomeLayout isDark={isDark} toggleTheme={toggleTheme}>
              <div className="p-6 max-w-4xl mx-auto space-y-6">
                <h1 className="text-3xl font-bold">Methodology</h1>
                
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold">Location Strategy: Does It Matter?</h2>
                  <div className="p-4 bg-card rounded-xl border border-border">
                    <p className="text-muted-foreground mb-4">
                      <strong>Short answer: No.</strong> There is no mathematical advantage to buying from different stores or machines.
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                      <li>Ticket rolls are randomly distributed from the lottery commission</li>
                      <li>No store has "lucky" machines - this is a cognitive bias</li>
                      <li>High-traffic stores cycle inventory faster (no advantage)</li>
                      <li>The ONLY edge is choosing the RIGHT GAME based on remaining prizes</li>
                    </ul>
                  </div>
                  
                  <h2 className="text-xl font-semibold mt-6">Why We Focus on Top 3 Prize Tiers</h2>
                  <div className="p-4 bg-card rounded-xl border border-border">
                    <p className="text-muted-foreground mb-4">
                      <strong>Forget the jackpot.</strong> The odds are astronomical and not worth chasing.
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                      <li><strong>Jackpot ($100K+):</strong> 1 in 100,000+ odds - pure luck</li>
                      <li><strong>Tier 2 ($1K-$10K):</strong> 1 in 1,000-5,000 odds - attainable</li>
                      <li><strong>Tier 3 ($100-$500):</strong> 1 in 100-500 odds - realistic target</li>
                      <li><strong>Tier 4 ($50-$100):</strong> 1 in 20-100 odds - frequent enough to track</li>
                    </ul>
                  </div>
                  
                  <h2 className="text-xl font-semibold mt-6">Data Sources</h2>
                  <div className="p-4 bg-card rounded-xl border border-border">
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                      <li><strong>State Lottery Official:</strong> Prize tier data, remaining prizes</li>
                      <li><strong>ScratchOdds.com:</strong> Total tickets printed, remaining tickets, velocity data</li>
                      <li><strong>Daily Updates:</strong> Data refreshed automatically every 24 hours</li>
                    </ul>
                  </div>
                </div>
              </div>
            </HomeLayout>
          } />
        </Routes>
        <Toaster position="top-right" theme={isDark ? "dark" : "light"} />
        <DisclaimerModal />
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <StateProvider>
      <AppContent />
    </StateProvider>
  );
}

export default App;
